import { getFirestore, doc, setDoc, getDoc, collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { BusinessProfile, IdealClient, ValueProp, BizSnapshot } from '../types/biz';
import { toBizSnapshot, normalizeOldProfileFields } from './biz.mappers';
import app from '../firebase';

/**
 * Loads business profile for a user
 */
export async function loadBusiness(uid: string): Promise<BusinessProfile | null> {
  const db = getFirestore(app);
  
  try {
    // Try to load from profiles collection (which has permissions)
    const profileRef = doc(db, "profiles", uid);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const profileData = profileSnap.data();
      const normalizedData = normalizeOldProfileFields(profileData);
      
      if (normalizedData.companyName) {
        // Return business data from profile
        const business: BusinessProfile = {
          uid,
          companyName: normalizedData.companyName,
          companySize: normalizedData.companySize || 'solo',
          industry: normalizedData.industry || [],
          regions: normalizedData.regions || [],
          mode: normalizedData.mode || 'online',
          languages: normalizedData.languages || [],
          description: normalizedData.description,
          website: normalizedData.website,
          kvkNumber: normalizedData.kvkNumber,
          socials: normalizedData.socials,
          createdAt: normalizedData.createdAt || Date.now(),
          updatedAt: Date.now()
        };
        
        return business;
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error loading business profile:", error);
    return null;
  }
}

/**
 * Saves business profile to profiles collection
 */
/**
 * Recursively removes undefined values from an object (including nested objects)
 */
const removeUndefinedFields = <T extends any>(obj: T): T => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedFields(item)) as T;
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        // Recursively clean nested objects
        cleaned[key] = removeUndefinedFields(value);
      }
    }
    return cleaned as T;
  }
  
  return obj;
};

export async function saveBusiness(uid: string, data: Partial<BusinessProfile>): Promise<void> {
  const db = getFirestore(app);
  const profileRef = doc(db, "profiles", uid);
  
  try {
    const now = Date.now();
    
    // Get current profile first
    const profileSnap = await getDoc(profileRef);
    const currentProfile = profileSnap.exists() ? profileSnap.data() : { uid };
    
    // Update profile with business data - ensure all fields are properly formatted
    // Remove undefined values to prevent Firestore errors
    const updatedProfile: any = {
      ...currentProfile,
      uid: uid, // Ensure uid is always set
      companyName: data.companyName || '',
      companySize: data.companySize || 'solo',
      industry: Array.isArray(data.industry) ? data.industry : [],
      regions: Array.isArray(data.regions) ? data.regions : [],
      mode: data.mode || 'online',
      languages: Array.isArray(data.languages) ? data.languages : [],
      lastOnboardingStep: "business",
      updatedAt: new Date().toISOString()
    };
    
    // Only add optional fields if they have values (avoid undefined)
    if (data.description !== undefined && data.description !== null && data.description !== '') {
      updatedProfile.description = data.description;
    }
    if (data.website !== undefined && data.website !== null && data.website !== '') {
      updatedProfile.website = data.website;
    }
    if (data.kvkNumber !== undefined && data.kvkNumber !== null && data.kvkNumber !== '') {
      updatedProfile.kvkNumber = data.kvkNumber;
    }
    if (data.socials !== undefined && data.socials !== null) {
      // Clean nested undefined values from socials object
      const cleanedSocials = removeUndefinedFields(data.socials);
      if (Object.keys(cleanedSocials).length > 0) {
        updatedProfile.socials = cleanedSocials;
      }
    }
    
    // Recursively remove any undefined values from the profile object (Firestore doesn't accept undefined)
    const cleanedProfile = removeUndefinedFields(updatedProfile);
    
    console.log("💾 Attempting to save business profile:", {
      uid,
      profileRef: profileRef.path,
      dataKeys: Object.keys(cleanedProfile),
      regions: cleanedProfile.regions,
      industry: cleanedProfile.industry,
      hasSocials: 'socials' in cleanedProfile
    });
    
    await setDoc(profileRef, cleanedProfile, { merge: true });
    console.log("✅ Business profile saved successfully to profiles collection");
  } catch (error: any) {
    console.error("❌ Error saving business profile:", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      uid,
      data: data
    });
    throw error;
  }
}

/**
 * Loads ideal clients for a user from profiles collection
 */
export async function loadIdealClients(uid: string): Promise<IdealClient[]> {
  const db = getFirestore(app);
  
  try {
    const profileRef = doc(db, "profiles", uid);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const profileData = profileSnap.data();
      const idealClients = profileData.idealClients || [];
      
      if (idealClients.length > 0) {
        return idealClients;
      }
    }
    
    return [];
  } catch (error) {
    console.error("Error loading ideal clients:", error);
    return [];
  }
}

/**
 * Saves ideal client to profiles collection
 */
export async function saveIdealClient(uid: string, id: string | null, data: Partial<IdealClient>): Promise<string> {
  const db = getFirestore(app);
  
  try {
    const now = Date.now();
    const idealClientData: IdealClient = {
      id: id || crypto.randomUUID(),
      uid,
      title: data.title || '',
      sectors: data.sectors || [],
      companySizeRange: data.companySizeRange,
      roles: data.roles || [],
      geo: data.geo || 'NL',
      budgetRange: data.budgetRange || 'mid',
      pains: data.pains || [],
      goals: data.goals || [],
      mustHaves: data.mustHaves || [],
      niceToHaves: data.niceToHaves || [],
      antiPersona: data.antiPersona || [],
      avgDealValue: data.avgDealValue,
      createdAt: data.createdAt || now,
      updatedAt: now
    };
    
    // Get current profile
    const profileRef = doc(db, "profiles", uid);
    const profileSnap = await getDoc(profileRef);
    const currentProfile = profileSnap.exists() ? profileSnap.data() : { uid };
    
    // Get existing ideal clients and clean them
    const existingIdealClients = (currentProfile.idealClients || []).map((ic: any) => removeUndefinedFields(ic));
    
    const cleanedIdealClientData = removeUndefinedFields(idealClientData);
    let updatedIdealClients;
    if (id) {
      // Update existing
      updatedIdealClients = existingIdealClients.map((ic: any) => 
        ic.id === id ? cleanedIdealClientData : removeUndefinedFields(ic)
      );
    } else {
      // Add new
      updatedIdealClients = [...existingIdealClients, cleanedIdealClientData];
    }
    
    // Clean the entire array
    updatedIdealClients = updatedIdealClients.map((ic: any) => removeUndefinedFields(ic));
    
    // Update profile
    const updatedProfile = {
      ...currentProfile,
      idealClients: updatedIdealClients,
      lastOnboardingStep: "business",
      updatedAt: new Date().toISOString()
    };
    
    // Recursively remove undefined values from the entire profile
    const cleanedProfile = removeUndefinedFields(updatedProfile);
    await setDoc(profileRef, cleanedProfile, { merge: true });
    console.log("✅ Ideal client saved successfully to profiles collection");
    return idealClientData.id;
  } catch (error) {
    console.error("❌ Error saving ideal client:", error);
    throw error;
  }
}

/**
 * Loads value propositions for a user from profiles collection
 */
export async function loadValueProps(uid: string): Promise<ValueProp[]> {
  const db = getFirestore(app);
  
  try {
    const profileRef = doc(db, "profiles", uid);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      const profileData = profileSnap.data();
      const valueProps = profileData.valueProps || [];
      
      if (valueProps.length > 0) {
        return valueProps;
      }
    }
    
    return [];
  } catch (error) {
    console.error("Error loading value propositions:", error);
    return [];
  }
}

/**
 * Saves value proposition to profiles collection
 */
export async function saveValueProp(uid: string, id: string | null, data: Partial<ValueProp>): Promise<string> {
  const db = getFirestore(app);
  
  try {
    const now = Date.now();
    const valuePropData: ValueProp = {
      id: id || crypto.randomUUID(),
      uid,
      promise: data.promise || '',
      differentiators: data.differentiators || [],
      proof: data.proof || [],
      elevator: data.elevator || '',
      offers: data.offers || [],
      caseLinks: data.caseLinks || [],
      createdAt: data.createdAt || now,
      updatedAt: now
    };
    
    // Get current profile
    const profileRef = doc(db, "profiles", uid);
    const profileSnap = await getDoc(profileRef);
    const currentProfile = profileSnap.exists() ? profileSnap.data() : { uid };
    
    // Get existing value propositions and clean them
    const existingValueProps = (currentProfile.valueProps || []).map((vp: any) => removeUndefinedFields(vp));
    
    const cleanedValuePropData = removeUndefinedFields(valuePropData);
    let updatedValueProps;
    if (id) {
      // Update existing
      updatedValueProps = existingValueProps.map((vp: any) => 
        vp.id === id ? cleanedValuePropData : removeUndefinedFields(vp)
      );
    } else {
      // Add new
      updatedValueProps = [...existingValueProps, cleanedValuePropData];
    }
    
    // Clean the entire array
    updatedValueProps = updatedValueProps.map((vp: any) => removeUndefinedFields(vp));
    
    // Update profile
    const updatedProfile = {
      ...currentProfile,
      valueProps: updatedValueProps,
      lastOnboardingStep: "business",
      updatedAt: new Date().toISOString()
    };
    
    // Recursively remove undefined values from the entire profile
    const cleanedProfile = removeUndefinedFields(updatedProfile);
    await setDoc(profileRef, cleanedProfile, { merge: true });
    console.log("✅ Value proposition saved successfully to profiles collection");
    return valuePropData.id;
  } catch (error) {
    console.error("❌ Error saving value proposition:", error);
    throw error;
  }
}

/**
 * Updates profile business snapshot
 */
export async function updateProfileBizSnapshot(uid: string): Promise<void> {
  const db = getFirestore(app);
  
  try {
    // Load business profile
    const business = await loadBusiness(uid);
    if (!business) {
      console.log("No business profile found, skipping snapshot update");
      return;
    }
    
    // Load most recent ideal client
    const idealClients = await loadIdealClients(uid);
    const mostRecentIdealClient = idealClients[0];
    
    // Load most recent value proposition
    const valueProps = await loadValueProps(uid);
    const mostRecentValueProp = valueProps[0];
    
    // Create snapshot
    const snapshot = toBizSnapshot(business, mostRecentIdealClient, mostRecentValueProp);
    
    // Save to profile
    const profileRef = doc(db, "profiles", uid);
    await setDoc(profileRef, { 
      biz: snapshot,
      lastOnboardingStep: "business",
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    console.log("✅ Business snapshot updated successfully");
  } catch (error) {
    console.error("❌ Error updating business snapshot:", error);
    throw error;
  }
}

